import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/auth";

const PRESET_AMOUNTS = [2500, 5000, 10000, 20000]; // pence: £25, £50, £100, £200

export default function GiftVouchers() {
  const [amountPence, setAmountPence] = useState(5000);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "",
    recipient_name: "", recipient_email: "", message: "",
  });
  const [busy, setBusy] = useState(false);

  const currentAmount = customAmount
    ? Math.max(1000, Math.round(Number(customAmount) * 100))
    : amountPence;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name.trim()) return toast.error("Enter your name");
    if (!form.buyer_email.trim()) return toast.error("Enter your email");
    if (currentAmount < 1000) return toast.error("Minimum voucher is £10");

    setBusy(true);
    try {
      const r = await axios.post(`${API}/gift-vouchers/checkout`, {
        amount_pence: currentAmount,
        buyer_email: form.buyer_email.trim(),
        buyer_name: form.buyer_name.trim(),
        recipient_email: form.recipient_email.trim() || null,
        recipient_name: form.recipient_name.trim() || null,
        message: form.message.trim() || null,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-gift-vouchers" className="pt-32 pb-24">
      <div className="mx-auto max-w-[900px] px-6 sm:px-10">
        <div className="text-center">
          <div className="overline">Give the gift of hair</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight text-balance">
            Malek Dridi Gift Vouchers.
          </h1>
          <p className="mt-6 text-[hsl(var(--onyx)/0.7)] max-w-xl mx-auto leading-relaxed">
            The perfect present for anyone in your life — for extensions, training, NøoksProRange, or just a treat-yourself moment. Delivered by email, redeemable across the whole site.
          </p>
        </div>

        <form onSubmit={submit} className="mt-12 rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-6 sm:p-10 space-y-8" data-testid="voucher-form">
          <div>
            <div className="overline flex items-center gap-2"><Gift size={14} /> Amount</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRESET_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => { setAmountPence(amt); setCustomAmount(""); }}
                  data-testid={`voucher-amount-${amt}`}
                  className={`h-14 rounded-2xl border text-lg font-display transition-colors ${
                    amountPence === amt && !customAmount
                      ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] border-[hsl(var(--onyx))]"
                      : "bg-white border-[hsl(var(--onyx)/0.15)] hover:border-[hsl(var(--onyx))]"
                  }`}
                >
                  £{amt / 100}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Or a custom amount (min £10)</label>
              <div className="relative mt-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--onyx)/0.5)]">£</span>
                <input
                  type="number" min="10" max="1000" step="1"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  data-testid="voucher-custom-amount"
                  placeholder="Custom £"
                  className="w-full h-12 pl-8 pr-4 rounded-2xl border border-[hsl(var(--onyx)/0.15)] text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Your name</label>
              <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                data-testid="voucher-buyer-name"
                className="w-full h-12 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Your email</label>
              <input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                data-testid="voucher-buyer-email"
                className="w-full h-12 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
            </div>
          </div>

          <div className="pt-4 border-t border-[hsl(var(--onyx)/0.08)]">
            <div className="overline">Recipient (optional)</div>
            <p className="text-xs text-[hsl(var(--onyx)/0.55)] mt-2">
              Leave blank to have the voucher sent to you, or add someone's details to gift it directly.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Recipient name</label>
                <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  data-testid="voucher-recipient-name"
                  className="w-full h-12 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Recipient email</label>
                <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                  data-testid="voucher-recipient-email"
                  className="w-full h-12 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Personal message</label>
              <textarea value={form.message} rows={3} maxLength={500}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="voucher-message"
                placeholder="Happy Birthday! Enjoy every second — from Nadia x"
                className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y" />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="voucher-submit"
            className="btn-pill btn-primary w-full disabled:opacity-60"
          >
            {busy ? "Redirecting to Stripe…" : `Buy voucher · £${(currentAmount / 100).toFixed(2)}`}
            <ArrowUpRight size={16} />
          </button>
          <p className="text-[11px] text-center text-[hsl(var(--onyx)/0.5)]">
            Secure checkout by Stripe. Pay in 3 with Klarna or 4 with Clearpay.
          </p>
        </form>
      </div>
    </div>
  );
}
